/**
 * What an approval on the consent page will grant — the client-side mirror of
 * `computeGrantedScopes` in `server/src/cf/oauth/scopes.ts`:
 *
 *   granted = requested ∩ supported ∩ chosen
 *
 * `requested` is what the server's describe endpoint returns (the app's request, already
 * narrowed to supported scopes). `chosen` follows the user's two choices: Read vs Read and
 * change, and the AI-memory toggle. The server stays the authority — it recomputes the grant
 * from the same inputs on approve — this only keeps the page from promising what it will
 * not hand out.
 *
 * Pure functions (the only import is a type), so they can be tested on their own.
 */

import type { ConsentAccess } from "./oauthService"

/** Resource families in the order they are listed, with the label shown for each. */
export const RESOURCE_LABELS: ReadonlyArray<{ resource: string; label: string }> = [
  { resource: "tasks", label: "To-dos" },
  { resource: "containers", label: "Projects and lists" },
  { resource: "areas", label: "Areas" },
  { resource: "reminders", label: "Reminders" },
  { resource: "tags", label: "Tags" },
  { resource: "memory", label: "AI memory" },
]

/** Same rule as `chosenScopes` on the server: read always, write only for read-write, memory only when asked. */
export function isScopeChosen(scope: string, access: ConsentAccess, includeMemory: boolean): boolean {
  const [resource, action] = scope.split(":")
  if (resource === "memory" && !includeMemory) return false
  return action === "read" || (action === "write" && access === "read-write")
}

/** requested ∩ chosen, in the order the app asked for them. */
export function grantedScopes(
  requested: readonly string[],
  access: ConsentAccess,
  includeMemory: boolean,
): string[] {
  return requested.filter((scope) => isScopeChosen(scope, access, includeMemory))
}

export interface ScopeRow {
  label: string
  /** The resource family has a `:write` scope in the set. */
  canWrite: boolean
}

/** One row per resource family that has any scope in `scopes`, in display order. */
export function scopeRows(scopes: readonly string[]): ScopeRow[] {
  return RESOURCE_LABELS.filter(({ resource }) => scopes.some((s) => s.startsWith(`${resource}:`))).map(
    ({ resource, label }) => ({ label, canWrite: scopes.includes(`${resource}:write`) }),
  )
}

export interface ConsentSummary {
  /** What approving would grant right now. */
  granted: ScopeRow[]
  /** Labels of whole resource families the app asked for that the choices leave out. */
  withheldResources: string[]
  /** The app asked to change data somewhere that is granted read-only. */
  withheldChanges: boolean
}

/** The page's view of a request: what is granted, and what was asked for but is not included. */
export function summarizeConsent(
  requested: readonly string[],
  access: ConsentAccess,
  includeMemory: boolean,
): ConsentSummary {
  const grantedList = grantedScopes(requested, access, includeMemory)
  const granted = scopeRows(grantedList)
  const grantedLabels = new Set(granted.map((row) => row.label))
  const withheldResources = scopeRows(requested)
    .filter((row) => !grantedLabels.has(row.label))
    .map((row) => row.label)
  const withheldChanges = requested.some(
    (scope) =>
      scope.endsWith(":write") &&
      !grantedList.includes(scope) &&
      grantedLabels.has(
        RESOURCE_LABELS.find(({ resource }) => scope.startsWith(`${resource}:`))?.label ?? "",
      ),
  )
  return { granted, withheldResources, withheldChanges }
}
